import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
	listApiKeys,
	createApiKey,
	revokeApiKey,
	revokeAllApiKeys,
	type ApiKey,
} from '@/services/api-keys';

export function ApiKeysSection() {
	const qc = useQueryClient();
	const [creating, setCreating] = useState(false);
	const [name, setName] = useState('');
	const [createdToken, setCreatedToken] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const { data: keys = [], isLoading } = useQuery({
		queryKey: ['api-keys'],
		queryFn: listApiKeys,
	});

	const refresh = () => qc.invalidateQueries({ queryKey: ['api-keys'] });

	const createMut = useMutation({
		mutationFn: (keyName: string) => createApiKey(keyName),
		onSuccess: (data) => {
			setCreatedToken(data.token);
			setCreating(false);
			setName('');
			refresh();
			toast.success('API key created — copy it now, it won’t be shown again');
		},
		onError: (e: Error) => toast.error(e.message),
	});

	const revokeMut = useMutation({
		mutationFn: (id: string) => revokeApiKey(id),
		onSuccess: () => {
			refresh();
			toast.success('API key revoked');
		},
		onError: (e: Error) => toast.error(e.message),
	});

	const revokeAllMut = useMutation({
		mutationFn: () => revokeAllApiKeys(),
		onSuccess: (data) => {
			refresh();
			toast.success(data.revoked > 0 ? `Revoked ${data.revoked} API key(s)` : 'No active API keys');
		},
		onError: (e: Error) => toast.error(e.message),
	});

	const copyToken = async () => {
		if (!createdToken) return;
		await navigator.clipboard.writeText(createdToken);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const handleCreate = () => {
		if (!name.trim()) {
			toast.error('Give the key a name');
			return;
		}
		createMut.mutate(name.trim());
	};

	return (
		<section>
			<h2 className="text-lg font-semibold mb-1">API keys</h2>
			<p className="text-sm text-muted-foreground mb-6">
				Personal access tokens for scripts and AI assistants (MCP). A key can do
				anything your account can — treat it like a password and revoke it if it leaks.
			</p>

			{createdToken && (
				<div className="mb-4 p-3 border rounded-lg bg-amber-50 dark:bg-amber-950/20 space-y-2">
					<p className="text-xs font-medium text-amber-700 dark:text-amber-300">
						Copy this key now — it won’t be shown again.
					</p>
					<div className="flex items-center gap-2">
						<code className="flex-1 font-mono text-xs bg-background border rounded px-2 py-1.5 break-all select-all">
							{createdToken}
						</code>
						<Button variant="outline" size="sm" onClick={copyToken} className="shrink-0">
							{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
							{copied ? 'Copied' : 'Copy'}
						</Button>
					</div>
					<Button variant="ghost" size="sm" onClick={() => setCreatedToken(null)}>
						Done
					</Button>
				</div>
			)}

			{!creating && (
				<Button
					size="sm"
					onClick={() => {
						setCreatedToken(null);
						setCreating(true);
					}}
					disabled={createMut.isPending}
				>
					<Plus className="h-4 w-4 mr-2" />
					Create key
				</Button>
			)}

			{creating && (
				<div className="flex items-end gap-2">
					<div className="space-y-1.5 flex-1">
						<Label htmlFor="key-name">Key name</Label>
						<Input
							id="key-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. claude-mcp, phone, ci"
							autoFocus
							onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
						/>
					</div>
					<Button onClick={handleCreate} disabled={createMut.isPending}>
						{createMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
						Create
					</Button>
					<Button variant="ghost" onClick={() => setCreating(false)}>
						Cancel
					</Button>
				</div>
			)}

			<div className="mt-6 space-y-2">
				{isLoading ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
						<Loader2 className="h-4 w-4 animate-spin" /> Loading keys…
					</div>
				) : keys.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4">
						No API keys yet. Create one to connect scripts or an MCP client.
					</p>
				) : (
					keys.map((key: ApiKey) => (
						<div
							key={key.id}
							className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 ${
								key.revoked ? 'opacity-50' : ''
							}`}
						>
							<div className="min-w-0">
								<div className="flex items-center gap-2">
									<span className={`text-sm font-medium truncate ${key.revoked ? 'line-through' : ''}`}>
										{key.name}
									</span>
									<code className="text-xs text-muted-foreground font-mono">…{key.last4}</code>
									{key.revoked && (
										<span className="text-[10px] uppercase tracking-wide text-destructive">revoked</span>
									)}
								</div>
								<p className="text-xs text-muted-foreground">
									Created {new Date(key.created_at).toLocaleDateString()}
									{key.last_used_at
										? ` · last used ${new Date(key.last_used_at).toLocaleString()}`
										: ' · never used'}
								</p>
							</div>
							{!key.revoked && (
								<Button
									variant="ghost"
									size="sm"
									disabled={revokeMut.isPending}
									onClick={() => {
										if (window.confirm(`Revoke API key "${key.name}"?`)) {
											revokeMut.mutate(key.id);
										}
									}}
								>
									<Trash2 className="h-4 w-4 text-destructive" />
								</Button>
							)}
						</div>
					))
				)}
			</div>

			{keys.some((k: ApiKey) => !k.revoked) && (
				<Button
					variant="outline"
					size="sm"
					className="mt-4 text-destructive border-destructive/40"
					disabled={revokeAllMut.isPending}
					onClick={() => {
						if (window.confirm('Revoke ALL API keys? Any MCP/script using them will stop working immediately.')) {
							revokeAllMut.mutate();
						}
					}}
				>
					Revoke all keys
				</Button>
			)}
		</section>
	);
}
